import {
  ArrowLeft,
  Check,
  DotsThree,
  MagnifyingGlass,
  UserMinus,
  Users,
  X,
} from '@phosphor-icons/react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import { usePersonNameFormatter } from '@/lib/users/use-person-name'
import { buildFriendIndex } from '@/lib/users/person-name'
import { useFriendIndexStore } from '@/lib/users/friend-index-store'
import { formatNumber } from '@/lib/i18n/format'
import { kuniriIconWeight } from '@/lib/icons'

import {
  profileSocialApi,
  type Friend,
  type FriendSearchItem,
  type FriendsResponse,
} from '../api'
import { ConfirmDialog } from '../components/confirm-dialog'
import { PullToRefresh } from '../components/pull-to-refresh'
import { RefreshButton } from '../components/refresh-button'
import { UserAvatar } from '../components/user-avatar'
import { useDebouncedValue, useIsDesktop } from '../hooks'

const MIN_QUERY_LENGTH = 2

type FriendsState =
  | { status: 'loading' }
  | { status: 'ready'; data: FriendsResponse }
  | { status: 'error'; error: unknown }

type SearchState = {
  query: string
  attempt: number
  status: 'ready' | 'error'
  items: FriendSearchItem[]
}

type SearchPhase = 'idle' | 'loading' | 'ready' | 'error'

interface NoticeState {
  tone: 'success' | 'error'
  key: string
}

function sharedActivitiesText(
  count: number,
  t: (key: string, values?: Record<string, unknown>) => string,
): string | null {
  if (count <= 0) return null
  return count === 1
    ? t('profileSocial.friends.sharedActivitiesOne')
    : t('profileSocial.friends.sharedActivities', { count })
}

/**
 * `/friends` (UI_PARITY §3.13). A single tap opens a profile; incoming
 * requests are decided immediately from the row, and empty request sections
 * stay hidden. The remove action lives in the row overflow menu.
 */
export function FriendsScreen() {
  const { t, i18n } = useTranslation()
  // D-101: pending requests and search hits are not friends yet — abbreviated.
  const formatPerson = usePersonNameFormatter()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const searchId = useId()
  const locale = i18n.language === 'en' ? 'en' : 'it'

  const [state, setState] = useState<FriendsState>({ status: 'loading' })
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search.trim(), 300)
  const [searchState, setSearchState] = useState<SearchState>({
    query: '',
    attempt: 0,
    status: 'ready',
    items: [],
  })
  const [searchAttempt, setSearchAttempt] = useState(0)
  const [reloadKey, setReloadKey] = useState(0)
  const hasDataRef = useRef(false)
  const [refreshing, setRefreshing] = useState(false)
  const [notice, setNotice] = useState<NoticeState | null>(null)
  const [requestBusyId, setRequestBusyId] = useState<string | null>(null)
  const [removeTarget, setRemoveTarget] = useState<Friend | null>(null)
  const [removeBusy, setRemoveBusy] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    void profileSocialApi
      .listFriends({ signal: controller.signal })
      .then((data) => {
        // The friends page is the source of truth; keep the D-101 index in
        // sync so names here never flip back to the abbreviated form.
        useFriendIndexStore.getState().seed(buildFriendIndex(data))
        if (controller.signal.aborted) return
        hasDataRef.current = true
        setState({ status: 'ready', data })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        if (hasDataRef.current) {
          setNotice({ tone: 'error', key: friendlyErrorKey(error) })
        } else {
          setState({ status: 'error', error })
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setRefreshing(false)
      })
    return () => controller.abort()
  }, [reloadKey])

  useEffect(() => {
    if (debouncedSearch.length < MIN_QUERY_LENGTH) return
    const controller = new AbortController()
    void profileSocialApi
      .searchFriends(debouncedSearch, { signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) {
          setSearchState({
            query: debouncedSearch,
            attempt: searchAttempt,
            status: 'ready',
            items: response.items,
          })
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setSearchState({
            query: debouncedSearch,
            attempt: searchAttempt,
            status: 'error',
            items: [],
          })
        }
      })
    return () => controller.abort()
  }, [debouncedSearch, searchAttempt])

  const reload = useCallback(() => {
    setRefreshing(true)
    setReloadKey((key) => key + 1)
  }, [])

  const refresh = reload

  const retry = useCallback(() => {
    setState({ status: 'loading' })
    setReloadKey((key) => key + 1)
  }, [])

  const decideRequest = useCallback(
    async (kind: 'accept' | 'reject', friend: Friend) => {
      if (requestBusyId) return
      setRequestBusyId(friend.friendshipId)
      setNotice(null)
      try {
        if (kind === 'accept') {
          const response = await profileSocialApi.acceptFriendRequest(
            friend.friendshipId,
          )
          setNotice({
            tone: response.status === 'accepted' ? 'success' : 'error',
            key:
              response.status === 'accepted'
                ? 'profileSocial.friends.acceptSuccess'
                : 'profileSocial.friends.requestGone',
          })
        } else {
          await profileSocialApi.rejectFriendRequest(friend.friendshipId)
          setNotice({
            tone: 'success',
            key: 'profileSocial.friends.rejectSuccess',
          })
        }
        if (kind === 'accept') {
          useFriendIndexStore.getState().refresh()
        }
        reload()
      } catch (error) {
        setNotice({ tone: 'error', key: friendlyErrorKey(error) })
      } finally {
        setRequestBusyId(null)
      }
    },
    [reload, requestBusyId],
  )

  const removeFriend = useCallback(async () => {
    if (!removeTarget) return
    setRemoveBusy(true)
    setRemoveError(null)
    try {
      await profileSocialApi.removeFriend(removeTarget.userId)
      setNotice({
        tone: 'success',
        key: 'profileSocial.friends.removeSuccess',
      })
      setRemoveTarget(null)
      useFriendIndexStore.getState().refresh()
      reload()
    } catch (error) {
      setRemoveError(friendlyErrorKey(error))
    } finally {
      setRemoveBusy(false)
    }
  }, [reload, removeTarget])

  const data = state.status === 'ready' ? state.data : null
  const searchActive = debouncedSearch.length >= MIN_QUERY_LENGTH
  const searchPhase: SearchPhase = !searchActive
    ? 'idle'
    : searchState.query !== debouncedSearch ||
        searchState.attempt !== searchAttempt
      ? 'loading'
      : searchState.status

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="min-h-dvh bg-paper px-4 pt-[calc(var(--safe-top)+16px)] pb-[calc(1.5rem+var(--safe-bottom))] md:px-8 md:py-6"
    >
      <div className="mx-auto w-full max-w-[960px]">
        <div className="flex items-center gap-2">
          <Link
            to="/profile"
            className="grid size-11 shrink-0 place-items-center rounded-full text-deep hover:bg-mint"
            aria-label={t('common.back')}
          >
            <ArrowLeft size={22} weight="bold" aria-hidden="true" />
          </Link>
          <h1 className="mr-auto text-[23px] font-extrabold">
            {t('screens.friends.pageTitle')}
          </h1>
          {isDesktop ? (
            <RefreshButton
              onRefresh={refresh}
              refreshing={refreshing || state.status === 'loading'}
              labelKey="profileSocial.friends.refresh"
            />
          ) : null}
        </div>

        {notice ? (
          <p
            role={notice.tone === 'error' ? 'alert' : 'status'}
            className={`mt-3 rounded-md px-4 py-3 text-sm font-semibold ${
              notice.tone === 'error'
                ? 'bg-[var(--danger-soft)] text-danger'
                : 'bg-mint text-teal'
            }`}
          >
            {t(notice.key)}
          </p>
        ) : null}

        <section aria-labelledby="find-people-heading" className="mt-4">
          <h2 id="find-people-heading" className="text-[23px] font-extrabold">
            {t('screens.friends.title')}
          </h2>
          <label htmlFor={searchId} className="sr-only mt-3">
            {t('profileSocial.friends.searchLabel')}
          </label>
          <div className="relative mt-2">
            <MagnifyingGlass
              size={19}
              weight={kuniriIconWeight}
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted"
            />
            <input
              id={searchId}
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('profileSocial.friends.searchPlaceholder')}
              autoComplete="off"
              className="field-input pl-10"
            />
          </div>
          <p className="mt-1 text-xs text-muted">
            {t('profileSocial.friends.searchHint')}
          </p>
        </section>

        <PullToRefresh
          className="mt-2"
          onRefresh={refresh}
          refreshing={refreshing}
          disabled={isDesktop}
        >
          {searchActive ? (
            <SearchResults
              phase={searchPhase}
              items={searchState.items}
              onRetry={() => setSearchAttempt((value) => value + 1)}
              onOpenProfile={(item) =>
                navigate(`/users/${encodeURIComponent(item.id)}`)
              }
              locale={locale}
            />
          ) : null}

          {state.status === 'loading' ? (
            <LoadingState labelKey="profileSocial.friends.loading" />
          ) : state.status === 'error' ? (
            <div className="mt-5">
              <ErrorState
                error={state.error}
                titleKey="profileSocial.friends.loadErrorTitle"
                bodyKey="profileSocial.friends.loadErrorBody"
                onRetry={retry}
                retryLabelKey="common.retry"
              />
            </div>
          ) : data ? (
            <div className="mt-5 space-y-6">
              {data.incoming.length > 0 ? (
                <section aria-labelledby="incoming-heading">
                  <h2 id="incoming-heading" className="text-lg font-extrabold">
                    {t('profileSocial.friends.incomingHeading', {
                      count: data.incoming.length,
                    })}
                  </h2>
                  <ul className="mt-2 divide-y divide-line overflow-hidden rounded-[22px] border border-line bg-white">
                    {data.incoming.map((friend) => (
                      <li
                        key={friend.friendshipId}
                        className="flex items-center gap-2 p-2 pr-3"
                      >
                        <Link
                          to={`/users/${encodeURIComponent(friend.userId)}`}
                          className="flex min-w-0 flex-1 items-center gap-3 rounded-md p-1 hover:bg-mint"
                        >
                          <PersonSummary
                            name={formatPerson(friend)}
                            friend={friend}
                            shared={sharedActivitiesText(
                              friend.sharedActivities,
                              t,
                            )}
                          />
                        </Link>
                        <div className="flex shrink-0 gap-1">
                          <button
                            type="button"
                            className="grid size-13 place-items-center rounded-full bg-mint text-teal hover:bg-[var(--mint-hover)]"
                            aria-label={t('profileSocial.friends.reject')}
                            disabled={requestBusyId === friend.friendshipId}
                            onClick={() => void decideRequest('reject', friend)}
                          >
                            <X size={20} weight="bold" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="grid size-13 place-items-center rounded-full bg-teal text-on-teal hover:bg-[var(--teal-hover)]"
                            aria-label={t('profileSocial.friends.accept')}
                            disabled={requestBusyId === friend.friendshipId}
                            aria-busy={requestBusyId === friend.friendshipId}
                            onClick={() => void decideRequest('accept', friend)}
                          >
                            <Check size={20} weight="bold" aria-hidden="true" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {data.outgoing.length > 0 ? (
                <section aria-labelledby="outgoing-heading">
                  <h2 id="outgoing-heading" className="text-lg font-extrabold">
                    {t('profileSocial.friends.outgoingHeading', {
                      count: data.outgoing.length,
                    })}
                  </h2>
                  <ul className="mt-2 divide-y divide-line overflow-hidden rounded-[22px] border border-line bg-white">
                    {data.outgoing.map((friend) => (
                      <li key={friend.friendshipId}>
                        <Link
                          to={`/users/${encodeURIComponent(friend.userId)}`}
                          className="flex items-center gap-3 p-3 hover:bg-mint"
                        >
                          <PersonSummary
                            name={formatPerson(friend)}
                            friend={friend}
                            shared={sharedActivitiesText(
                              friend.sharedActivities,
                              t,
                            )}
                          />
                          <span className="ml-auto shrink-0 text-sm font-semibold text-muted">
                            {t('profileSocial.friends.pending')}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              <section aria-labelledby="friends-heading">
                <h2 id="friends-heading" className="text-lg font-extrabold">
                  {t('profileSocial.friends.friendsHeading', {
                    count: data.friends.length,
                  })}
                </h2>
                {data.friends.length === 0 ? (
                  <p className="mt-2 text-sm text-muted">
                    {t('profileSocial.friends.emptyFriends')}
                  </p>
                ) : (
                  <ul className="mt-2 divide-y divide-line overflow-hidden rounded-[22px] border border-line bg-white">
                    {data.friends.map((friend) => (
                      <li
                        key={friend.friendshipId}
                        className="flex items-center gap-1 p-1 pr-2"
                      >
                        <Link
                          to={`/users/${encodeURIComponent(friend.userId)}`}
                          className="flex min-w-0 flex-1 items-center gap-3 rounded-md p-2 hover:bg-mint"
                        >
                          <PersonSummary
                            name={formatPerson(friend)}
                            friend={friend}
                            shared={sharedActivitiesText(
                              friend.sharedActivities,
                              t,
                            )}
                          />
                        </Link>
                        <FriendMenu
                          name={friend.publicName}
                          onRemove={() => {
                            setRemoveError(null)
                            setRemoveTarget(friend)
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          ) : null}
        </PullToRefresh>
      </div>

      {removeTarget ? (
        <ConfirmDialog
          open
          title={t('profileSocial.friends.removeTitle', {
            name: removeTarget.publicName,
          })}
          body={t('profileSocial.friends.removeBody', {
            name: removeTarget.publicName,
          })}
          confirmLabel={t('profileSocial.friends.remove')}
          busy={removeBusy}
          danger
          errorKey={removeError}
          onConfirm={() => void removeFriend()}
          onClose={() => setRemoveTarget(null)}
        />
      ) : null}
    </main>
  )
}

function PersonSummary({
  name,
  friend,
  shared,
}: {
  name: string
  friend: Friend
  shared: string | null
}) {
  return (
    <>
      <UserAvatar name={name} src={friend.avatarUrl} size="md" />
      <span className="min-w-0">
        <span className="block truncate font-bold text-deep">
          {name}
        </span>
        <span className="block truncate text-sm text-muted">
          @{friend.nickname}
        </span>
        {shared ? (
          <span className="flex items-center gap-1 text-xs font-semibold text-teal">
            <Users size={13} weight={kuniriIconWeight} aria-hidden="true" />
            {shared}
          </span>
        ) : null}
      </span>
    </>
  )
}

function FriendMenu({
  name,
  onRemove,
}: {
  name: string
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        className="grid size-11 place-items-center rounded-full text-deep hover:bg-mint"
        aria-label={t('profileSocial.friends.rowActions', { name })}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <DotsThree size={22} weight="bold" aria-hidden="true" />
      </button>
      {open ? (
        <div className="absolute top-11 right-0 z-10 w-44 rounded-md border border-line bg-white p-1 shadow-[var(--shadow-float)]">
          <button
            type="button"
            className="flex min-h-11 w-full items-center gap-2 rounded-sm px-3 text-left text-sm font-bold text-danger hover:bg-[var(--danger-soft)]"
            onClick={() => {
              setOpen(false)
              onRemove()
            }}
          >
            <UserMinus size={17} weight={kuniriIconWeight} aria-hidden="true" />
            {t('profileSocial.friends.remove')}
          </button>
        </div>
      ) : null}
    </div>
  )
}

interface SearchResultsProps {
  phase: SearchPhase
  items: FriendSearchItem[]
  locale: 'it' | 'en'
  onRetry: () => void
  onOpenProfile: (item: FriendSearchItem) => void
}

function SearchResults({
  phase,
  items,
  locale,
  onRetry,
  onOpenProfile,
}: SearchResultsProps) {
  const { t } = useTranslation()
  // D-101: only accepted friends appear in full names; everyone else is
  // abbreviated until the friend index knows better.
  const formatPerson = usePersonNameFormatter()
  return (
    <section aria-labelledby="search-heading" className="mt-4">
      <h2 id="search-heading" className="text-lg font-extrabold">
        {t('profileSocial.friends.searchResults')}
      </h2>
      {phase === 'loading' ? (
        <p role="status" className="mt-2 text-sm text-muted">
          {t('profileSocial.friends.searching')}
        </p>
      ) : phase === 'error' ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted">
          <span>{t('profileSocial.friends.searchError')}</span>
          <button type="button" className="btn-ghost min-h-11" onClick={onRetry}>
            {t('common.retry')}
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="mt-2 text-sm text-muted">
          {t('profileSocial.friends.noResults')}
        </p>
      ) : (
        <>
          <ul className="mt-2 divide-y divide-line overflow-hidden rounded-[22px] border border-line bg-white">
            {items.map((item) => {
              const pending = item.friendStatus === 'pending'
              const accepted = item.friendStatus === 'accepted'
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 p-3 text-left hover:bg-mint"
                    onClick={() => onOpenProfile(item)}
                  >
                    <UserAvatar
                      name={formatPerson(item)}
                      src={item.avatarUrl}
                      size="md"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-bold text-deep">
                        {formatPerson(item)}
                      </span>
                      <span className="block truncate text-sm text-muted">
                        @{item.nickname}
                      </span>
                      {item.sharedActivities > 0 ? (
                        <span className="block text-xs font-semibold text-teal">
                          {item.sharedActivities === 1
                            ? t('profileSocial.friends.sharedActivitiesOne')
                            : t('profileSocial.friends.sharedActivities', {
                                count: formatNumber(
                                  item.sharedActivities,
                                  locale,
                                ),
                              })}
                        </span>
                      ) : null}
                    </span>
                    {accepted ? (
                      <span className="shrink-0 text-sm font-semibold text-teal">
                        {t('profileSocial.friends.alreadyFriend')}
                      </span>
                    ) : pending ? (
                      <span className="shrink-0 text-sm font-semibold text-muted">
                        {t('profileSocial.friends.pending')}
                      </span>
                    ) : null}
                  </button>
                </li>
              )
            })}
          </ul>
          <p className="mt-2 text-xs text-muted">
            {t('profileSocial.friends.searchHelper')}
          </p>
        </>
      )}
    </section>
  )
}
